import { selectCurrentHotelId, selectHotelList, selectIsInternalAdmin } from 'accessors'
import { updateCampaign } from 'actions/campains'
import {
  PrimarySmallButton,
  SecondarySmallButton
} from 'components/ReusableComponents/Button/SmallButtons'
import AddLink from 'components/ReusableComponents/Icons/AddLink'
import Toggle from 'components/ReusableComponents/Toggle'
import {
  features,
  useUnlockService
} from 'components/ReusableComponents/UnlockServiceDialog'
import useClickOut from 'hooks/useClickOut'
import usePlausible from 'hooks/usePlausible'
import { campaignHierarchy } from 'main/campaignHierarchy'
import { DEFAULT_SUBSIDIARY_ID } from 'main/globalVariables'
import moment from 'moment'
import { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import campaignApi from 'services/api/campaign'

import s from './CampaignCard.module.scss'
import ExternalSmsDialog from './ExternalSmsDialog'
import { useCampaignName } from './useCampaignName'

const CampaignRow = ({
  campaigns,
  regularCampaign,
  OTACampaign,
  category,
  type,
  isDefaultCampaigns,
  isLocked,
  isImportant,
  updatedOn,
  updatedBy,
  alternateReviewLink
}) => {
  const [isTestSmsLoading, setIsTestSmsLoading] = useState(false)
  const [showTestDialog, setShowTestDialog] = useState(false)
  const [showExternalSmsDialog, setShowExternalSmsDialog] = useState(false)

  const [campaignName, campaignDesc] = useCampaignName(regularCampaign.category)

  const hasAlternativeReviewLink =
    regularCampaign.category === 'CheckedOutReview' && !!alternateReviewLink

  const { addToast } = useToasts()

  const userPhone = useSelector(s => s.login.currentUser.phone)

  const lockedCategories = [
    'Lost6Months',
    'Lost9Months',
    'Lost12Months',
    'Lost15Months',
    'AfterLastVisit',
    'OTAAfterVisit'
  ]

  const lockedHotels = [176, 180, 181, 182, 189]

  const currentHotelId = useSelector(selectCurrentHotelId)
  const hotelList = useSelector(selectHotelList)
  const isInternalAdmin = useSelector(selectIsInternalAdmin)
  const isHotelSetupCompleted = useSelector(s => s.hotelDetails.isPreOnboardingCompleted)

  const currentHotelDisplayId = hotelList.find(
    h => h.subsidiaryId === currentHotelId
  ).displayId

  const isEditHidden =
    lockedCategories.includes(category) &&
    lockedHotels.includes(currentHotelDisplayId) &&
    !isInternalAdmin

  const dispatch = useDispatch()

  const contentSubsidiaryId = isDefaultCampaigns ? DEFAULT_SUBSIDIARY_ID : currentHotelId

  const isActive = regularCampaign.isActive

  const { postEvent, plausibleEvents } = usePlausible()

  const switchCampaignToggle = async () => {
    if (!isHotelSetupCompleted) {
      addToast(
        'Your hotel setup is not completed yet. Complete your setup to enable campaigns.',
        {
          appearance: 'error',
          autoDismiss: true
        }
      )

      return
    }

    postEvent(
      isActive ? plausibleEvents.DisableCampaign : plausibleEvents.EnableCampaign,
      {
        category
      }
    )

    if (!hasMarketingService) {
      openUnlockServiceDialog(features.Campaigns)
      return
    }

    dispatch(
      updateCampaign(
        contentSubsidiaryId,
        campaigns.map(c => c.category),
        {
          isActive: !isActive
        }
      )
    )
  }

  const OTAVariantCategory = campaignHierarchy?.[category]?.OTAVariant

  const sentTestSms = async category => {
    setIsTestSmsLoading(true)
    setShowTestDialog(false)
    campaignApi
      .sendTestSms({ contentCategory: category })
      .then(() => {
        addToast(`We've sent a test message to ${userPhone}`, {
          appearance: 'success',
          autoDismiss: true
        })
        setShowExternalSmsDialog(false)
      })
      .catch(() => {
        addToast(`Failed to sent a test message to ${userPhone}`, {
          appearance: 'error',
          autoDismiss: true
        })
      })
      .finally(() => {
        setIsTestSmsLoading(false)
      })
  }

  const sentTestSmsToAnotherNumber = async data => {
    setIsTestSmsLoading(true)
    setShowTestDialog(false)
    campaignApi
      .sendTestSmsToAnotherNumber(data)
      .then(() => {
        addToast(`We've sent a test message to ${userPhone}`, {
          appearance: 'success',
          autoDismiss: true
        })
        setShowExternalSmsDialog(false)
      })
      .catch(err => {
        addToast(` ${err}`, {
          appearance: 'error',
          autoDismiss: true
        })
      })
      .finally(() => {
        setIsTestSmsLoading(false)
      })
  }

  const handleExternalSmsDialogClose = () => {
    setShowExternalSmsDialog(false)
  }

  const testDialogRef = useRef()
  useClickOut(testDialogRef, () => setShowTestDialog(false))

  const { hasMarketingService, openUnlockServiceDialog } = useUnlockService()

  return (
    <div
      className={s.card}
      title={`Updated by ${updatedBy} on ${moment(updatedOn).format(
        'DD/MM/YYYY hh:mm A'
      )}`}
      style={{ border: isImportant ? '1px solid #0079ff' : 'none' }}
    >
      <div className={s.header}>
        <hgroup style={s.hgroup}>
          <h3 className={s.name}>{campaignName}</h3>
          {campaignDesc && <p className={s.description}>{campaignDesc}</p>}
        </hgroup>
        <Toggle
          className={s.toggle}
          labelClass={s.toggleLabel}
          label={isActive ? 'On' : 'Off'}
          checked={isActive}
          handleChange={switchCampaignToggle}
          disabled={isLocked}
        />
      </div>
      <div className={s.controls}>
        {hasAlternativeReviewLink && (
          <AddLink
            width={30}
            height={30}
            style={{ marginRight: 'auto', fill: '#0079ff' }}
          />
        )}
        <SecondarySmallButton
          disabled={!isActive || isTestSmsLoading}
          onClick={() => (OTACampaign ? setShowTestDialog(true) : sentTestSms(category))}
        >
          {isTestSmsLoading ? 'Sending...' : 'Test'}
          {showTestDialog && (
            <div className={s.testDialog} ref={testDialogRef}>
              <SecondarySmallButton onClick={() => sentTestSms(category)}>
                Direct guest variant
              </SecondarySmallButton>
              <SecondarySmallButton onClick={() => sentTestSms(OTAVariantCategory)}>
                OTA variant
              </SecondarySmallButton>
              {isInternalAdmin && (
                <SecondarySmallButton onClick={() => setShowExternalSmsDialog(true)}>
                  Send to another number
                </SecondarySmallButton>
              )}
            </div>
          )}
        </SecondarySmallButton>
        {!isEditHidden && (
          <Link
            to={`/${
              isDefaultCampaigns ? 'editDefaultCampaign' : 'editCampaign'
            }/${type}/${category}`}
          >
            <PrimarySmallButton>Edit content</PrimarySmallButton>
          </Link>
        )}
        {showExternalSmsDialog && (
          <ExternalSmsDialog
            sentTestSmsToAnotherNumber={sentTestSmsToAnotherNumber}
            category={category}
            OTAVariantCategory={OTAVariantCategory}
            isTestSmsLoading={isTestSmsLoading}
            handleClose={handleExternalSmsDialogClose}
          />
        )}
      </div>
    </div>
  )
}

export default CampaignRow
